/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Input, OnInit} from '@angular/core';
import * as _ from 'lodash';

import template from './domain-targeting.component.html';
import {ADN_TARGETING_TYPES} from '../targeting-constants';
import {TargetingTemplateHook} from './targeting-template.component';

interface DomainEntry {
  val: string;
}

import {TARGETING_IMPORTS} from '../targeting-imports';
import {TargetingTemplateComponent} from './targeting-template.component';

@Component({
  imports: [...TARGETING_IMPORTS, TargetingTemplateComponent],
  selector: 'adn-domain-targeting',
  template
})
export class DomainTargetingComponent implements OnInit {
  @Input() model: any;
  @Input() tabController: any;
  @Input() overridableTargeting: any;
  @Input() targetType: any = ADN_TARGETING_TYPES.domain;
  @Input() underModel: any;
  @Input() mode: string;
  @Input() isLockedDown: boolean;
  @Input() mandatory: boolean;

  loaded = true;
  inputted: {value: DomainEntry[]} = {value: []};

  readonly templateHook: TargetingTemplateHook = {
    getTemplateData: () => {
      this.prepareForSave();
      const summary = (this.model.negated === true ? 'NOT: ' : '') + (this.model.names || []).join(', ');
      return {
        vm: _.cloneDeep(this.inputted),
        model: _.cloneDeep(this.model),
        constant: ADN_TARGETING_TYPES.domain,
        summary
      };
    },
    copyTemplate: (tpl: any) => this.copyTemplate(tpl)
  };

  ngOnInit(): void {
    this.init();

    if (!this.tabController) {
      return;
    }

    const prepareForSave = () => this.prepareForSave();
    const copyTemplate = (tpl: any) => this.copyTemplate(tpl);
    this.tabController.register(ADN_TARGETING_TYPES.domain.widget, () => prepareForSave(), copyTemplate);
    this.tabController.callBeforeSubmit(prepareForSave);
    this.tabController.registerAllTabs(prepareForSave);
  }

  addDomain(): void {
    this.inputted.value = this.inputted.value || [];
    this.inputted.value.push({val: ''});
  }

  removeDomain(index: number): void {
    if (index === 0 && _.get(this.inputted, ['value'], []).length < 2) {
      _.set(this.inputted.value, [index, 'val'], '');
      return;
    }
    _.pullAt(this.inputted.value, index);
  }

  private init(): void {
    const inputted: DomainEntry[] = [];
    _.forEach(_.get(this.model, 'names'), (domain) => {
      inputted.push({val: domain});
    });
    if (inputted.length === 0) {
      inputted.push({val: ''});
    }
    this.inputted = {value: inputted};
    this.model = this.model || {names: [], negated: false};
    this.model.negated = this.model.negated || false;
  }

  private prepareForSave(): void {
    const isValidDomainName = (data: string) => {
      const re = /^(\*\.)?([\w\d-]+\.)*[\w\d]+$/;
      return re.test(data);
    };

    const values = _.map(this.inputted.value, 'val');
    const trimmedValues = _.filter(_.map(values, (v) => _.trim(v)), (v) => _.isString(v) && v.length > 0 && isValidDomainName(v));

    this.model = this.model || {names: []};
    this.model.names = trimmedValues;
    this.model.negated = this.model.negated || false;
    this.init();
  }

  private copyTemplate(tpl: any): void {
    const copied = _.cloneDeep(_.get(tpl, ['data', 'model'])) || {names: [], negated: false};
    Object.keys(this.model).forEach((key) => delete this.model[key]);
    Object.assign(this.model, copied);
    this.init();
  }
}
